import { createPortal } from "react-dom";
import { FiCalendar, FiX } from "react-icons/fi";
import { useMemo } from "react";

interface AvaliationDateSelectorModalProps {
  isOpen: boolean;
  dates: string[];
  selectedDate: string | null;
  onChange: (date: string) => void;
  onClose: () => void;
}

export default function AvaliationDateSelectorModal({
  isOpen,
  dates,
  selectedDate,
  onChange,
  onClose,
}: AvaliationDateSelectorModalProps) {
  const sortedDates = useMemo(() => {
    return [...dates].sort(
      (a, b) => new Date(b).getTime() - new Date(a).getTime()
    );
  }, [dates]);

  if (!isOpen) return null;

  const effectiveSelected =
    selectedDate && sortedDates.includes(selectedDate)
      ? selectedDate
      : sortedDates[0] || "";

  const handleChange = (value: string) => {
    onChange(value);
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999999] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="relative w-full max-w-md rounded-lg border border-stroke bg-white shadow-default outline-none dark:border-strokedark dark:bg-boxdark">
        <div className="flex items-center justify-between border-b border-stroke px-6 py-4 dark:border-strokedark">
          <div className="flex items-center gap-2">
            <FiCalendar className="text-primary" size={22} />
            <div>
              <h3 className="text-lg font-semibold text-black dark:text-white">
                Selecionar data da avaliação
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                A última avaliação cadastrada é selecionada automaticamente.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-2 text-gray-500 outline-none transition hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-primary dark:text-gray-400 dark:hover:bg-meta-4"
            aria-label="Fechar seleção de datas"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="px-6 py-5">
          {sortedDates.length === 0 ? (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Nenhuma avaliação encontrada para este aluno.
            </p>
          ) : (
            <div className="space-y-3">
              <label
                htmlFor="avaliation-date-select"
                className="block text-sm font-medium text-black dark:text-white"
              >
                Data da avaliação
              </label>
              <div className="relative">
                <select
                  id="avaliation-date-select"
                  value={effectiveSelected}
                  onChange={(e) => handleChange(e.target.value)}
                  className="w-full appearance-none rounded-lg border border-stroke bg-transparent px-4 py-2.5 text-sm text-black outline-none transition focus:border-primary focus:ring-1 focus:ring-primary dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
                >
                  {sortedDates.map((date) => (
                    <option key={date} value={date}>
                      {new Date(date).toLocaleDateString("pt-BR")}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
                  <svg
                    className="h-4 w-4 fill-current"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="M7 10l5 5 5-5H7z" />
                  </svg>
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-stroke px-6 py-4 dark:border-strokedark">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-stroke px-5 py-2 text-sm font-medium text-black outline-none transition hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-primary dark:border-strokedark dark:text-white dark:hover:bg-meta-4"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

